import posthog from 'posthog-js'

export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig()
  const route = useRoute()

  console.log('[PostHog] Initializing with:', {
    host: window.location.origin + '/api/posthog',
    target: config.public.POSTHOG_HOST,
    key: config.public.POSTHOG_KEY?.substring(0, 20) + '...' // Only show first 20 chars for security
  })

  // PostHog configuration - Optimized session recording to prevent 413 errors
  const posthogConfig = {
    api_host: window.location.origin + '/api/posthog', // Use local proxy to avoid CORS
    ui_host: config.public.POSTHOG_HOST, // Important for toolbar authentication
    autocapture: true,
    session_recording: {
      // Balanced optimization - server-side limits will be fixed
      maskAllInputs: false, // Restore normal input recording
      maskInputOptions: {
        password: true,
        email: false, // Restore email recording
        creditCard: true,
      },
      recordCrossOriginIframes: false,
      blockClass: 'ph-no-capture',
      // Block heavy elements but allow most content
      blockSelector: 'script,style',
      collectFonts: false, // Still reduce payload
      recordCanvas: false, // Still skip canvas for performance
      recordHeaders: false, // Skip headers for security
      recordBody: false, // Skip request bodies for security
      // Reasonable data collection settings
      sampling: {
        sessionSamplingRate: 0.5, // Record 50% of sessions
      },
      // Reasonable chunking and timing
      checkoutEveryNms: 10 * 1000, // Send chunks every 10 seconds
      maxSessionMs: 30 * 60 * 1000, // 30 minutes max session
      ignoreClass: 'rr-ignore', // Allow easy element ignoring
      maskTextClass: 'rr-mask', // Allow text masking
      maskTextSelector: 'input,textarea,select', // Mask form elements
    },
    capture_pageview: false, // We'll handle this manually
    capture_pageleave: true,
    persistence: 'localStorage' as const,
    property_blacklist: [],
    bootstrap: {
      distinctID: undefined,
    },
    debug: false, // Explicitly disable debug mode
    // Re-enable session recording with aggressive optimization to prevent 413 errors
    disable_session_recording: false, // Session recording enabled with optimizations
    disable_compression: true, // Disable to fix net::ERR_CONTENT_DECODING_FAILED
    disable_surveys: false,
    // Handle CORS gracefully - fallback to defaults if remote config fails
    on_request_error: (error: any) => {
      if (error.message?.includes('CORS') || error.message?.includes('Access-Control-Allow-Origin')) {
        console.log('[PostHog] CORS issue with remote config - using local defaults (all features enabled)')
      } else {
        console.error('[PostHog] Request error:', error)
      }
    },
    loaded: (posthog: any) => {
      console.log('[PostHog] Successfully loaded and initialized')
      
      // Check all feature statuses with safer API calls
      try {
        const sessionRecordingEnabled = posthog.sessionRecording && typeof posthog.sessionRecording.status === 'function' 
          ? posthog.sessionRecording.status() !== 'disabled'
          : posthog.config?.disable_session_recording === false
        
        const autocaptureEnabled = posthog.config?.autocapture
        const compressionEnabled = posthog.config?.disable_compression === false
        
        console.log('[PostHog] Feature status:', {
          sessionRecording: sessionRecordingEnabled,
          autocapture: autocaptureEnabled,
          compression: compressionEnabled ? 'Enabled' : 'Disabled (for stability)',
          toolbar: 'Ready for launch from PostHog UI',
          surveys: 'Enabled',
          featureFlags: 'Enabled',
          errorTracking: 'Enabled'
        })
        
        // CRITICAL: Disable debug mode explicitly to clear localStorage flag
        // Based on PostHog issue #1387, debug mode is stored in localStorage
        // and needs explicit posthog.debug(false) to clear it
        if (typeof posthog.debug === 'function') {
          posthog.debug(false) // This clears the debug flag from localStorage
        }
        
        // Also disable verbose logging if available
        if (typeof posthog.set_config === 'function') {
          posthog.set_config({ 
            verbose: false,
            debug: false 
          })
        }
        
        // Check for toolbar parameters in URL (for Nuxt compatibility)
        const toolbarJSON = new URLSearchParams(window.location.hash.substring(1)).get('__posthog')
        if (toolbarJSON) {
          console.log('[PostHog] Toolbar parameters detected, loading toolbar')
          try {
            posthog.loadToolbar(JSON.parse(toolbarJSON))
          } catch (error) {
            console.error('[PostHog] Failed to load toolbar:', error)
          }
        }
      } catch (error) {
        console.error('[PostHog] Error in loaded callback:', error)
      }
    },
  }

  try {
    // Initialize PostHog
    posthog.init(config.public.POSTHOG_KEY, posthogConfig)

    // Make PostHog available globally for toolbar and external scripts
    // This is required for the PostHog toolbar to function properly
    window.posthog = posthog

    // Store toolbar state globally for Nuxt navigation compatibility
    let storedToolbarJSON: string | null = null
    
    // Check for toolbar on initial load
    const checkForToolbar = () => {
      const toolbarJSON = new URLSearchParams(window.location.hash.substring(1)).get('__posthog')
      if (toolbarJSON && toolbarJSON !== storedToolbarJSON) {
        storedToolbarJSON = toolbarJSON
        console.log('[PostHog] Loading toolbar from URL')
        try {
          posthog.loadToolbar(JSON.parse(toolbarJSON))
        } catch (error) {
          console.error('[PostHog] Failed to load toolbar from URL:', error)
        }
      } else if (storedToolbarJSON && !toolbarJSON) {
        // Restore toolbar after navigation
        console.log('[PostHog] Restoring toolbar after navigation')
        try {
          posthog.loadToolbar(JSON.parse(storedToolbarJSON))
        } catch (error) {
          console.error('[PostHog] Failed to restore toolbar:', error)
        }
      }
    }

    // Check for toolbar on hash changes (toolbar navigation)
    window.addEventListener('hashchange', checkForToolbar)
    
    // Check for toolbar every 2 seconds in case of navigation issues
    setInterval(checkForToolbar, 2000)

    // Hook into Vue error handler for error tracking
    nuxtApp.hook('vue:error', (err: unknown, instance: any, info: string) => {
      console.error('[PostHog] Capturing Vue error:', err)
      
      // Convert unknown error to Error object
      const error = err instanceof Error ? err : new Error(String(err))
      
      // Capture the exception with additional context
      posthog.captureException(error, {
        source: 'vue:error',
        component: instance?.$options?.name || 'Unknown',
        route: route.fullPath,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        // Include component props if available
        props: instance?.$props,
        // Include any additional error info
        errorInfo: info || {}
      })
    })

    // Also capture unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      console.error('[PostHog] Capturing unhandled promise rejection:', event.reason)
      
      posthog.captureException(event.reason || new Error('Unhandled Promise Rejection'), {
        source: 'unhandledrejection',
        promise: event.promise,
        route: route.fullPath,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
      })
    })

    // Capture general JavaScript errors
    window.addEventListener('error', (event) => {
      console.error('[PostHog] Capturing JavaScript error:', event.error)
      
      posthog.captureException(event.error || new Error(event.message || 'Unknown Error'), {
        source: 'window:error',
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        route: route.fullPath,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
      })
    })

    // Test initial connection
    setTimeout(() => {
      try {
        // Track initial page view with more debugging
        console.log('[PostHog] Sending initial pageview for:', route.fullPath)
        posthog.capture('$pageview', {
          $current_url: route.fullPath,
          title: document.title,
          debug_test: true,
        })
        
        // Also send a test event
        posthog.capture('posthog_integration_test', {
          message: 'PostHog integration is working',
          timestamp: new Date().toISOString(),
          url: window.location.href,
          errorTrackingEnabled: true
        })
        
        console.log('[PostHog] Test events sent successfully')
        
        // Initial toolbar check
        checkForToolbar()
        
      } catch (error) {
        console.error('[PostHog] Failed to send initial events:', error)
      }
    }, 1000) // Give PostHog a moment to initialize

  } catch (error) {
    console.error('[PostHog] Initialization failed:', error)
  }

  // Make PostHog available globally
  return {
    provide: {
      posthog: posthog
    }
  }
})
